import { useState } from "react";
import { eur, num, showMoney } from "@/lib/format";
import { ExpandToggle } from "@/components/ExpandToggle";
import { Card } from "@/components/ui/card";
import { ProvinceList } from "@/components/ProvinceList";
import { ItalyMap } from "@/components/ItalyMap";
import { MapPin, Globe, Map as MapIcon, List } from "lucide-react";

const TABS = [["regioni", "Regioni"], ["province", "Province"], ["citta", "Città"]];

const Bars = ({ items, testid, onPick, by }) => {
  const max = Math.max(...items.map((r) => r[by] || 0), 1);
  return (
    <div className="space-y-0.5" data-testid={testid}>
      {items.map((r) => (
        <button type="button" key={r.name} onClick={() => onPick(r.name)} title={`Vedi le clienti di ${r.name}`}
          className="block w-full rounded px-1.5 py-1 text-left text-sm transition-colors hover:bg-secondary/60" data-testid={`geo-row-${r.name}`}>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{r.name}</span>
            <span className="flex items-baseline gap-3">
              <span><span className="font-semibold">{num(r.value)}</span>{r.pct != null && <span className="ml-1.5 text-xs text-muted-foreground">{num(r.pct)}%</span>}</span>
              {showMoney() && <span className={`w-24 text-right text-xs ${by === "revenue" ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{eur(r.revenue)}</span>}
            </span>
          </div>
          <div className="mt-0.5 h-1 rounded bg-secondary"><div className="h-1 rounded bg-primary/70" style={{ width: `${((r[by] || 0) / max) * 100}%` }} /></div>
        </button>
      ))}
    </div>
  );
};

const Abroad = ({ items, onPick }) => {
  const total = items.reduce((s, i) => s + i.value, 0);
  return (
    <div className="rounded-md border border-dashed border-border/70 p-3" data-testid="customers-abroad">
      <p className="mb-2 flex items-center gap-1.5 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">
        <Globe className="h-3.5 w-3.5" />Estero <span className="normal-case tracking-normal">· {num(total)} clienti{showMoney() && ` · ${eur(items.reduce((s, i) => s + (i.revenue || 0), 0))}`}</span>
      </p>
      {items.length ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {items.map((c) => <button type="button" key={c.name} onClick={() => onPick(c.name)} className="text-muted-foreground hover:text-foreground hover:underline" title={showMoney() ? `Fatturato: ${eur(c.revenue)}` : c.name} data-testid={`geo-country-${c.name}`}>{c.name} <b className="text-foreground">{num(c.value)}</b></button>)}
        </div>
      ) : <p className="text-xs text-muted-foreground">Nessuna cliente all'estero.</p>}
    </div>
  );
};

const LIMIT = 10;
const ZONE = { regioni: "regione", province: "provincia", citta: "citta" };

export const CustomerGeo = ({ stats, onZone }) => {
  const [tab, setTab] = useState("regioni");
  const [all, setAll] = useState(false);
  const [by, setBy] = useState("value");
  const [map, setMap] = useState(false);
  const raw = { regioni: stats.by_region || [], province: stats.by_province || [], citta: stats.by_city || [] }[tab];
  const list = by === "revenue" ? [...raw].sort((a, b) => (b.revenue || 0) - (a.revenue || 0)) : raw;
  const shown = all ? list : list.slice(0, LIMIT);
  const hasMoney = raw.some((r) => r.revenue != null);
  const pick = (name) => onZone({ type: ZONE[tab], name });
  return (
    <Card className="p-5 lg:col-span-2" data-testid="customers-geo">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground"><MapPin className="h-3.5 w-3.5" />Dove sono le clienti</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setMap(!map)} data-testid="customers-geo-map-toggle"
            className="flex items-center gap-1.5 rounded-md border border-border/60 px-3 py-1 text-xs transition-colors hover:bg-secondary/60">
            {map ? <><List className="h-3.5 w-3.5" />Vedi elenco</> : <><MapIcon className="h-3.5 w-3.5" />Vedi sulla cartina</>}
          </button>
          <div className="flex gap-1 rounded-md bg-secondary p-1 text-xs">
          {TABS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setTab(k); setAll(false); }} data-testid={`customers-geo-${k}`}
              className={`rounded px-3 py-1 transition-colors ${tab === k ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_220px]">
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            {hasMoney ? (
              <span className="flex items-center gap-1 normal-case tracking-normal">Ordina per
                {[["value", "clienti"], ["revenue", "fatturato"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setBy(k)} data-testid={`customers-geo-sort-${k}`}
                    className={`rounded px-2 py-0.5 transition-colors ${by === k ? "bg-primary text-primary-foreground" : "bg-secondary hover:text-foreground"}`}>{l}</button>
                ))}
              </span>
            ) : <span />}
            <span className="flex gap-3"><span>Clienti</span><span className="w-24 text-right">Fatturato {stats.revenue_year}</span></span>
          </div>
          {map ? (
            <>
              <ItalyMap level={tab === "regioni" ? "regioni" : "province"} items={tab === "regioni" ? raw : stats.by_province || []} by={by} onPick={(name) => onZone({ type: tab === "regioni" ? "regione" : "provincia", name })} />
              {tab === "citta" && <p className="mt-1 text-center text-[11px] text-muted-foreground">Sulla cartina le città sono raggruppate per provincia.</p>}
            </>
          ) : (
            <>
              {tab === "province" ? <ProvinceList items={shown} bare onPick={pick} /> : <Bars items={shown} by={by} onPick={pick} testid={tab === "regioni" ? "customers-regions" : "customers-cities"} />}
              <ExpandToggle total={list.length} open={all} onToggle={() => setAll(!all)} limit={LIMIT} testid="customers-geo-toggle" />
            </>
          )}
          <p className="mt-2 text-[11px] text-muted-foreground">Clicca una voce{map ? " o una zona della cartina" : ""} per vedere l'elenco delle clienti di quella zona. Clienti con almeno un ordine{tab === "regioni" ? " in Italia, con % sul totale italiano" : ""}. Fatturato = speso nell'anno, netto resi.</p>
        </div>
        <div className="self-start"><Abroad items={stats.by_country || []} onPick={(name) => onZone({ type: "paese", name })} /></div>
      </div>
    </Card>
  );
};
